import {
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
  type PlaybookNode,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { useMemo, useState } from "react";
import type { Selection } from "react-aria-components";
import {
  type AssessmentTreeItemKey,
  deserializeAssessmentTreeItemKey,
  serializeAssessmentTreeItemKey,
} from "./assessmentTreeHelpers.js";

export function useAssessmentTreeSelection(props: {
  initialSelectedKey: AssessmentTreeItemKey | undefined;
  template: PlaybookTree;
}) {
  const { template } = props;

  const [selectedKey, setSelectedKey] = useState<
    AssessmentTreeItemKey | undefined
  >(props.initialSelectedKey);

  const selectedKeys = selectedKey
    ? [serializeAssessmentTreeItemKey(selectedKey)]
    : [];

  const onSelectionChange = (selection: Selection) => {
    if (selection === "all") {
      return;
    }

    const selectedKeys = Array.from(selection).map(
      deserializeAssessmentTreeItemKey,
    );
    const onlySelectedKey =
      selectedKeys.length === 1 ? selectedKeys[0] : undefined;
    setSelectedKey(onlySelectedKey);
  };

  const selectedNode = useMemo(() => {
    if (!selectedKey) return undefined;

    const id =
      selectedKey.type === "section" || selectedKey.type === "criteria"
        ? selectedKey.id
        : undefined;

    return id ? findNodeById(template.nodes, id) : undefined;
  }, [selectedKey, template.nodes]);

  const selectedSection =
    selectedKey?.type === "section" &&
    selectedNode &&
    isPlaybookSectionNode(selectedNode)
      ? selectedNode
      : undefined;

  const selectedCriteria =
    selectedKey?.type === "criteria" &&
    selectedNode &&
    isPlaybookCriterionNode(selectedNode)
      ? selectedNode
      : undefined;

  const selectedOutcome = selectedKey?.type === "outcome";
  const selectedKnowledgeBase = selectedKey?.type === "knowledge-base";
  const selectedSummaryTemplate = selectedKey?.type === "summary-template";

  // Ghost-criterion ids intentionally aren't in `template.nodes` — they're
  // proposals saved by the AI assistant. The caller resolves the id against
  // `useSampleToolResults().results` to get the proposal entry.
  const selectedGhostCriterionId =
    selectedKey?.type === "ghost-criterion" ? selectedKey.id : undefined;

  // Ghost-section ids also aren't in `template.nodes` — they're new-section
  // proposals saved by the AI assistant, resolved against
  // `useSampleToolResults().results`.
  const selectedGhostSectionId =
    selectedKey?.type === "ghost-section" ? selectedKey.id : undefined;

  return {
    // The raw selected key, for callers that need to react to selection changes.
    selectedKey,
    // For the Tree component
    selectedKeys,
    onSelectionChange,
    // For us
    selectedSection,
    selectedCriteria,
    selectedOutcome,
    selectedKnowledgeBase,
    selectedSummaryTemplate,
    selectedGhostCriterionId,
    selectedGhostSectionId,
    setSelectedKey,
  };
}

// Find the selected section or criteria
function findNodeById(
  nodes: PlaybookNode[],
  id: string,
): PlaybookNode | undefined {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (isPlaybookSectionNode(node)) {
      const found = findNodeById(node.nodes, id);
      if (found) return found;
    }
  }
  return undefined;
}
